import { useMemo } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';

import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { readTimeFromFeatures } from '../readTimeUtils.ts';
import type { ContentFeatures } from '../featuresExtractor.ts';
import { extractAllContentFeatures } from '../featuresExtractor.ts';

import type {
	useReadTime_content$data,
	useReadTime_content$key,
} from './__generated__/useReadTime_content.graphql.ts';
import type {
	useReadTimeQuery$data,
	useReadTimeQuery$variables,
} from './__generated__/useReadTimeQuery.graphql.ts';

export const ReadTime_content: GraphQLTaggedNode = graphql`
	fragment useReadTime_content on Content {
		id
		type
		body {
			dynamic {
				value
				representation
			}
		}
		operations {
			operation
			targetType
		}
	}
`;

export const ReadTimeQuery: GraphQLTaggedNode = graphql`
	query useReadTimeQuery($contentId: ID, $status: [String], $includeContentBody: Boolean = true) {
		content(id: $contentId, status: $status) {
			nodes {
				id
				type
				body @include(if: $includeContentBody) {
					dynamic {
						value
						representation
					}
				}
				operations {
					operation
					targetType
				}
			}
		}
	}
`;

type ReadTimeContentNode =
	| NonNullable<NonNullable<useReadTimeQuery$data['content']>['nodes']>[number]
	| useReadTime_content$data
	| null
	| undefined;

type ReadTimeResult = {
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	adf: JSONDocNode | undefined;
	contentType: string | null | undefined;
	readTime: number | null;
	wordCount: number | null;
	features: ContentFeatures | null;
	featuresSkipExpand: ContentFeatures | null;
	isReadTimeCapped: boolean;
};

type ReadTimeDerivedResult = Omit<ReadTimeResult, 'contentType' | 'loading' | 'error'>;

const getAdfStringFromContent = (content: ReadTimeContentNode): string | null => {
	const dynamic = content?.body?.dynamic;
	if (dynamic?.representation === 'atlas_doc_format' && dynamic?.value) {
		return dynamic.value;
	}

	return null;
};

const getReadTimeResult = ({
	content,
	contentId,
	loading,
	error,
}: {
	content: ReadTimeContentNode;
	contentId: string;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
}): ReadTimeDerivedResult => {
	const empty = {
		adf: undefined,
		readTime: null,
		wordCount: null,
		features: null,
		featuresSkipExpand: null,
		isReadTimeCapped: false,
	};

	const adfString = getAdfStringFromContent(content);
	if (!adfString || loading || error || !contentId) {
		return empty;
	}

	let parsed: JSONDocNode | null;
	try {
		parsed = JSON.parse(adfString) as JSONDocNode;
	} catch {
		parsed = null;
	}

	if (!parsed) {
		return empty;
	}

	const {
		features: allFeatures,
		featuresSkipExpand: allFeaturesSkipExpand,
		nodeCount,
	} = extractAllContentFeatures(parsed, adfString.length);
	const { readTime: computedReadTime, isReadTimeCapped: computedCapped } = readTimeFromFeatures(
		allFeaturesSkipExpand,
		nodeCount,
	);

	if (computedCapped) {
		return {
			adf: parsed,
			readTime: computedReadTime,
			wordCount: null,
			features: null,
			featuresSkipExpand: null,
			isReadTimeCapped: true,
		};
	}

	return {
		adf: parsed,
		readTime: computedReadTime,
		wordCount: allFeaturesSkipExpand.wordCount,
		features: allFeatures,
		featuresSkipExpand: allFeaturesSkipExpand,
		isReadTimeCapped: false,
	};
};

export const useReadTime = (contentId: string, skipQuery?: boolean): ReadTimeResult => {
	const readTimeQueryOptions = {
		variables: {
			contentId,
		},
		skip: skipQuery,
	};

	const { data, loading, error } = useQueryNoFurtherUpdateInterop<
		useReadTimeQuery$data,
		useReadTimeQuery$variables
	>(ReadTimeQuery, readTimeQueryOptions);

	const contentNode = data?.content?.nodes?.[0];
	const { adf, readTime, wordCount, features, featuresSkipExpand, isReadTimeCapped } = useMemo(
		() => getReadTimeResult({ content: contentNode, contentId, loading, error }),
		[contentNode, contentId, loading, error],
	);
	const contentType = contentNode?.type;

	return {
		loading,
		error,
		adf,
		contentType,
		readTime,
		wordCount,
		features,
		featuresSkipExpand,
		isReadTimeCapped,
	};
};

export const useReadTimeFromFragment = (
	contentId: string,
	contentFragmentRef: useReadTime_content$key,
): ReadTimeResult => {
	const content = useFragment(ReadTime_content, contentFragmentRef);
	const { adf, readTime, wordCount, features, featuresSkipExpand, isReadTimeCapped } = useMemo(
		() =>
			getReadTimeResult({
				content,
				contentId,
				loading: false,
				error: undefined,
			}),
		[content, contentId],
	);

	return {
		loading: false,
		error: undefined,
		adf,
		contentType: content?.type,
		readTime,
		wordCount,
		features,
		featuresSkipExpand,
		isReadTimeCapped,
	};
};
